import React, { FC, useEffect } from 'react';

import { NativeResources } from '@ariksa/inventory-core/api';
import { Grid, Stack } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { customTheme } from 'theme';
import { colorOpacity } from 'theme/utils';

import { DetailsIdentifier, formatBytes, Tag } from 'components/DataDisplay';
import { FootprintBox } from 'components/DataDisplay/Utils/FootprintBox';
import formatNumber from 'components/DataDisplay/Utils/formatNumber';
import { selectActiveResource } from 'containers/ActiveCloudResource/selectors';
import { actions } from 'containers/ActiveCloudResource/slice';

import { SensitiveDataBreakdown } from './SensitiveDataBreakdown';
import { getStatusLabel } from './utils';

export const SnowflakeTableDataOverview: FC = () => {
  const dispatch = useDispatch();
  const { resourceUuid, resourceDetails, data } = useSelector(
    selectActiveResource,
  );
  const { resourcesByGroup } = data;
  const {
    data: resourcesByGroupData,
    isLoading: isLoadingResourcesByGroup,
  } = resourcesByGroup;

  useEffect(() => {
    if (resourceUuid) {
      dispatch(
        actions.getResourcesByGroup({
          q: {
            nativeResource: NativeResources.SnowflakeTables,
            sourceUUIDs: [resourceUuid],
            page: 1,
            size: 1,
          },
        }),
      );
    }
  }, [dispatch, resourceUuid]);

  const scanResource = resourcesByGroupData?.items?.[0];
  const resource = resourceDetails.data;
  const config = resource?.config ?? {};
  const extraConfig = scanResource?.extra_config ?? {};
  const rowCount = config.rows;

  const tableSize = config.bytes ?? extraConfig.bytes ?? extraConfig.table_size;
  const replicationState =
    config.replication_state ??
    config.replication_status ??
    extraConfig.replication_state;
  const scanStatus = getStatusLabel(scanResource?.scan_status);

  return (
    <Grid
      templateColumns="repeat(3, minmax(0, 1fr))"
      gap={4}
      h={56}
      minH={0}
      flexShrink={0}
    >
      <FootprintBox
        label="Summary"
        isLoading={isLoadingResourcesByGroup}
        content={
          <Stack w="full" h="full" spacing={3}>
            <DetailsIdentifier
              label="Row Count"
              value={rowCount == null ? '-' : formatNumber(rowCount)}
            />
            <DetailsIdentifier
              label="Table Size"
              value={tableSize == null ? '-' : formatBytes(tableSize)}
            />
            <DetailsIdentifier
              label="Replication State"
              value={replicationState ?? '-'}
            />
            <DetailsIdentifier
              label="Status"
              value={
                scanStatus === '-' ? (
                  '-'
                ) : (
                  <Tag
                    label={scanStatus}
                    styles={{
                      tag: {
                        bg: colorOpacity(customTheme.colors.primary, 0.1),
                        border: 'none',
                      },
                      label: { color: customTheme.colors.primary },
                    }}
                  />
                )
              }
            />
          </Stack>
        }
      />
      <SensitiveDataBreakdown
        data={data.dataTypesBySource.data}
        resourceType={NativeResources.SnowflakeTables}
        isLoading={data.dataTypesBySource.isLoading}
      />
    </Grid>
  );
};
